import { LinkBuilder } from '../links/definition/linkBuilder';
import { applyState, toSuccess } from '../resolvers/definition/resolverState';
import { produceNext } from '../utils/immer';
import { applyDefaultsToEgg, SELF } from './resource';
import { defaultResourceRegistry } from './resourceRegistry';

// ─── Transform types (self = derived, other = source) ───────────────

// ─── Public step types ─────────────────────────────────────────────

/**
 * After `.mapParams` the derived resource has its own param shape, so its
 * identity can't be inherited from the source — declare it with `.mergeArgs`
 * or `.withArgs` before `.pull`. (Omit `.mapParams` entirely to keep the
 * source's params and inherit its identity.)
 */

// ─── Builder ────────────────────────────────────────────────────────

const buildDeriveChain = (name, source, paramMapper, pullTransform, derivedArgs, mergeWithSource) => {
  let hash;
  let pushTransform;
  let pushImmutableTransform;
  const deferredLinks = [];
  const register = (__registry = defaultResourceRegistry) => {
    var _source$args, _pushTransform;
    const inheritedArgs = mergeWithSource ? (_source$args = source.args) !== null && _source$args !== void 0 ? _source$args : [] : [];
    const mergedArgs = inheritedArgs.length > 0 || derivedArgs ? [...inheritedArgs, ...(derivedArgs !== null && derivedArgs !== void 0 ? derivedArgs : [])] : undefined;
    const resource = applyDefaultsToEgg({
      typeName: name,
      args: mergedArgs,
      hash,
      sources: [{
        resource: source,
        mapParams: paramMapper
      }]
    });

    // self = derived (resource), other = source
    const linkBuilder = new LinkBuilder(resource, source).findOther({
      fromSelfParams: ({
        params
      }) => paramMapper(params)
    }).pullState(({
      dataChanged,
      self,
      other
    }) => {
      var _other$state;
      if (!dataChanged || ((_other$state = other.state) === null || _other$state === void 0 ? void 0 : _other$state.data) === undefined) {
        return applyState(other.state, self.state);
      }
      const nextData = pullTransform({
        self: {
          params: self.params
        },
        other: {
          params: other.params,
          data: other.state.data
        }
      });
      return applyState(other.state, toSuccess(nextData)());
    });
    const pushFn = (_pushTransform = pushTransform) !== null && _pushTransform !== void 0 ? _pushTransform : pushImmutableTransform;
    const drafted = !!pushTransform;
    if (pushFn) {
      linkBuilder.pushState(({
        dataChanged,
        self,
        other
      }) => {
        var _self$state;
        if (!dataChanged || ((_self$state = self.state) === null || _self$state === void 0 ? void 0 : _self$state.data) === undefined || !other.state) {
          return;
        }
        const selfData = self.state.data;
        const run = sourceData => pushFn({
          self: {
            params: self.params,
            data: selfData
          },
          other: {
            params: other.params,
            data: sourceData
          }
        });
        const data = drafted ? produceNext(other.state.data, draft => run(draft)) : run(other.state.data);
        if (data !== undefined) {
          return applyState(other.state, toSuccess(data)());
        }
      });
    }
    const links = [linkBuilder.pullBehavior({
      state: true,
      invalidation: true
    }).pushBehavior({
      state: !!pushFn,
      invalidation: true
    }).finalize()];
    for (const deferred of deferredLinks) {
      const resolvedTarget = deferred.target === SELF ? resource : deferred.target;
      if (!resolvedTarget) {
        throw new Error('Target is undefined. Please check for an import cycle');
      }
      links.push(deferred.construct(new LinkBuilder(resource, resolvedTarget)).finalize());
    }
    __registry.registerResource(resource, links);
    return resource;
  };
  const linksResult = {
    register,
    linkTo: (linkTgt, construct) => {
      deferredLinks.push({
        target: linkTgt,
        construct: construct
      });
      return linksResult;
    }
  };
  const builder = Object.assign({
    push: fn => {
      pushTransform = fn;
      return builder;
    },
    pushImmutable: fn => {
      pushImmutableTransform = fn;
      return builder;
    },
    withHash: fn => {
      hash = fn;
      return builder;
    }
  }, linksResult);
  return builder;
};
export const deriveResource = name => ({
  from: source => {
    const buildPullStep = (paramMapper, args, mergeWithSource) => ({
      pull: transform => buildDeriveChain(name, source, paramMapper, transform, args, mergeWithSource),
      /**
       * Append these args to the source's args. Use when the derived resource
       * adds refinement keys on top of the source's identity (the common case).
       */
      mergeArgs: keys => buildPullStep(paramMapper, keys, true),
      /**
       * Use exactly these args, ignoring the source's args. Use when the
       * derived resource intentionally narrows identity (rare).
       */
      withArgs: keys => buildPullStep(paramMapper, keys, false)
    });

    // After `.mapParams` the derived has its own param shape, so identity must
    // be declared explicitly — no `.pull` until `.mergeArgs`/`.withArgs`.
    const buildMappedStep = paramMapper => ({
      mergeArgs: keys => buildPullStep(paramMapper, keys, true),
      withArgs: keys => buildPullStep(paramMapper, keys, false)
    });
    return {
      pull: buildPullStep(p => p, undefined, true).pull,
      mapParams: paramMapper => buildMappedStep(paramMapper)
    };
  }
});